import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ProMarketingLink from '../components/Marketing/ProMarketingLink';
import { localizedMetierNavPath } from '../config/marketingMetierNav';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  Calendar, 
  CreditCard, 
  Bell, 
  CheckCircle,
  Star,
  X,
  Smartphone,
  MessageCircle,
  CalendarPlus,
  Clock,
  User,
  FileText,
  Plus,
  Minus,
  TrendingUp,
  Dog,
  ArrowRight,
  Ellipsis,
  Home,
  LayoutGrid,
  Book,
  Users,
  DollarSign,
  Crown,
  HelpCircle,
  ChevronDown,
  Store,
  CircleDollarSign,
  Search,
} from 'lucide-react';

import SEOHead from '../components/SEO/SEOHead';
import {
  buildAggregateRatingSchema,
  buildSoftwareApplicationSchema,
  buildFAQPageSchema,
  buildOrganizationSchema,
  buildBreadcrumbSchema,
  buildWebSiteSchema,
} from '../components/SEO/schemas';
import {
  getLocaleFromPath,
  MARKETING_PATHS,
  marketingLocaleFromLanguage,
} from '../config/localeRoutes';
import { marketingHreflangCluster } from '../config/marketingHreflangClusters';

import SectionWrapper from '../components/Marketing/SectionWrapper';
import BentoGrid from '../components/Marketing/BentoGrid';
import BentoCard from '../components/Marketing/BentoCard';
import TrustBar from '../components/Marketing/TrustBar';
import AcacedTeaser from '../components/Marketing/AcacedTeaser';
import MarketingWorkflow from '../components/Marketing/MarketingWorkflow';
import PricingSection from '../components/Marketing/PricingSection';
import ConfigurationOfferSection from '../components/ConfigurationOfferSection';
import FAQSection from '../components/Marketing/FAQSection';
import MarketingCTA from '../components/Marketing/MarketingCTA';
import DemoModal from '../components/Marketing/DemoModal';
import './LandingPageV2.css';
import AppShowcaseVisual from '../components/Marketing/AppShowcaseVisual';
import { useHeroNotificationsPool } from '../components/Marketing/heroNotificationsPool';
import LandingWallOfLove from '../components/Marketing/LandingWallOfLove';
import MarketingPartnersStrip from '../components/Marketing/MarketingPartnersStrip';
import SocialProofStrip from '../components/Marketing/SocialProofStrip';
import BookingBridgeStrip from '../components/Marketing/BookingBridgeStrip';
import { useTranslation, Trans } from 'react-i18next';
import {
  buildLandingBenefits,
  buildLandingFaqItems,
  buildProfessionTabs,
  getLandingSchemaFeatures,
  type LandingBenefit,
} from './landingPageV2Copy';
import { LANDING_TOP_BLOG_GUIDES } from './landingTopBlogGuides';

const LP = 'landingV2';

const ProfessionVisuals = ({ type }: { type: string }) => {
  const { t } = useTranslation('marketing');
  const visuals = `${LP}.professions.visuals`;
  switch (type) {
    case 'pension': {
        type Stay = { from: number; to: number; animal: string; client: string };
        const pensionSections: { label: string; rows: { name: string; stays: Stay[] }[] }[] = [
          {
            label: t(`${visuals}.pension.zones.canine`),
            rows: [
              { name: 'Box 1', stays: [{ from: 2, to: 4, animal: 'Rex', client: 'Famille Dupont' }] },
              { name: 'Box 2', stays: [{ from: 3, to: 5, animal: 'Luna', client: 'M. Bernard' }] },
            ],
          },
          {
            label: t(`${visuals}.pension.zones.feline`),
            rows: [
              { name: 'Box 1', stays: [{ from: 2, to: 3, animal: 'Minette', client: 'Mme Martin' }] },
              { name: 'Box 2', stays: [{ from: 4, to: 5, animal: 'Félix', client: 'M. Durand' }] },
              { name: 'Box 3', stays: [] },
            ],
          },
        ];
        const dayHeaders = t(`${visuals}.pension.days`, { returnObjects: true }) as string[];
        const getCellAt = (stays: Stay[], col: number): Stay | null => stays.find(s => col >= s.from && col <= s.to) ?? null;
        const isStartOfStay = (stays: Stay[], col: number) => stays.some(s => s.from === col);
        return (
          <div style={{ padding: '20px', background: '#fff', borderRadius: '20px', border: '1px solid #e8e8e8', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', height: '100%', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div style={{ padding: '8px 16px', background: '#ec4899', color: 'white', borderRadius: '8px', fontWeight: 700, fontSize: '13px' }}>{t(`${visuals}.pension.createStay`)}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#64748b' }}>
                <Calendar size={14} />
                <span>{t(`${visuals}.pension.period`)}</span>
              </div>
            </div>
            <div className="lp-profession-table" style={{ border: '1px solid #e8e8e8', borderRadius: '10px', overflow: 'hidden', fontSize: '11px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '130px repeat(6, 1fr)', background: '#fafafa', fontWeight: 700, color: '#262626', borderBottom: '1px solid #e8e8e8' }}>
                <div style={{ padding: '10px 12px', borderRight: '1px solid #e8e8e8' }}>{t(`${visuals}.pension.unitsDays`)}</div>
                {dayHeaders.map((d, i) => (
                  <div key={d} style={{ padding: '10px 8px', textAlign: 'center', borderRight: i < 5 ? '1px solid #e8e8e8' : 'none' }}>{d}</div>
                ))}
              </div>
              {pensionSections.map((section, si) => (
                <React.Fragment key={si}>
                  <div style={{ padding: '8px 12px', background: '#f5f5f5', fontWeight: 700, color: '#595959', fontSize: '10px', borderBottom: '1px solid #e8e8e8' }}>{section.label}</div>
                  {section.rows.map((row, ri) => (
                    <div key={ri} style={{ display: 'grid', gridTemplateColumns: '130px repeat(6, 1fr)', borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ padding: '8px 12px', borderRight: '1px solid #e8e8e8' }}><span style={{ fontWeight: 500 }}>{row.name}</span></div>
                      {[0,1,2,3,4,5].map((c) => {
                        const stay = getCellAt(row.stays, c);
                        const start = stay && isStartOfStay(row.stays, c);
                        if (start && stay) {
                          const span = stay.to - stay.from + 1;
                          return (
                            <div
                              key={c}
                              style={{
                                gridColumn: `span ${span}`,
                                minHeight: '36px',
                                borderRight: '1px solid #f0f0f0',
                                background: 'rgba(236, 72, 153, 0.2)',
                                padding: '6px 10px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                borderLeft: '1px solid rgba(236,72,153,0.25)',
                              }}
                            >
                              <span style={{ fontWeight: 700, fontSize: '12px', color: '#be185d' }}>{stay.animal}</span>
                              <span style={{ fontSize: '10px', color: '#9d174d', marginTop: '1px' }}>{stay.client}</span>
                            </div>
                          );
                        }
                        if (stay) return null;
                        return <div key={c} style={{ minHeight: '36px', borderRight: c < 5 ? '1px solid #f0f0f0' : 'none' }} />;
                      })}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      }
    case 'educateur': {
        const suiviRows = t(`${visuals}.educator.rows`, { returnObjects: true }) as Array<{
          date: string;
          session: string;
          worked: string;
          comments: string;
        }>;
        return (
          <div style={{ padding: '24px', background: 'linear-gradient(180deg, #fafbff 0%, #fff 24px)', borderRadius: '20px', border: '1px solid #e0e7ff', boxShadow: '0 10px 40px rgba(99,102,241,0.08)', height: '100%', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px', paddingBottom: '14px', borderBottom: '2px solid #e0e7ff' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', boxShadow: '0 4px 12px rgba(99,102,241,0.35)' }}>🐕</div>
              <div>
                <div style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#312e81' }}>{t(`${visuals}.educator.title`)}</div>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6366f1', fontWeight: 600 }}>{t(`${visuals}.educator.subtitle`)}</p>
              </div>
            </div>
            <div className="lp-profession-table" style={{ border: '1px solid #e0e7ff', borderRadius: '14px', overflow: 'hidden', background: '#fff', boxShadow: '0 2px 8px rgba(99,102,241,0.06)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr 1.4fr 1.4fr', gap: '14px', padding: '14px 18px', background: 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)', fontWeight: 700, fontSize: '11px', color: 'rgba(255,255,255,0.95)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                <span>{t(`${visuals}.educator.columns.date`)}</span>
                <span>{t(`${visuals}.educator.columns.session`)}</span>
                <span>{t(`${visuals}.educator.columns.worked`)}</span>
                <span>{t(`${visuals}.educator.columns.comments`)}</span>
              </div>
              {suiviRows.map((row, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1.15fr 1.4fr 1.4fr',
                    gap: '14px',
                    padding: '12px 18px',
                    borderTop: '1px solid #eef2ff',
                    fontSize: '12px',
                    color: '#374151',
                    background: i % 2 === 0 ? '#fff' : '#f8fafc',
                  }}
                >
                  <span style={{ fontWeight: 600, color: '#4f46e5' }}>{row.date}</span>
                  <span style={{ color: '#475569' }}>{row.session}</span>
                  <span style={{ color: '#334155', lineHeight: 1.4 }}>{row.worked}</span>
                  <span style={{ color: '#475569', lineHeight: 1.4 }}>{row.comments}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }
    case 'promeneur': {
      const walkerLines = t(`${visuals}.walker.lines`, { returnObjects: true }) as string[];
      return (
        <div style={{ padding: '28px', background: '#fff', borderRadius: '20px', border: '1px solid #e8e8e8', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', height: '100%' }}>
          <div style={{ marginBottom: '16px', padding: '16px', background: 'linear-gradient(135deg, #1B4F4A 0%, #2C7A73 100%)', borderRadius: '12px', color: 'white', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Calendar size={20} color="white" />
            <div>
              <div style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>{t(`${visuals}.walker.title`)}</div>
              <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.9 }}>{t(`${visuals}.walker.subtitle`)}</p>
            </div>
          </div>
          <div style={{ border: '1px solid #f0f0f0', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ padding: '16px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, color: '#1f1f1f' }}>Marie Dupont</span>
              <span style={{ fontWeight: 700, color: '#1B4F4A', fontSize: '15px' }}>87,00 €</span>
            </div>
            {walkerLines.map((line, i) => (
              <div key={i} style={{ padding: '12px 16px', borderBottom: i < walkerLines.length - 1 ? '1px solid #f0f0f0' : 'none', fontSize: '13px', color: '#595959' }}>{line}</div>
            ))}
            <div style={{ padding: '16px', background: '#fafafa', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ padding: '10px 20px', background: '#1B4F4A', color: 'white', borderRadius: '8px', fontWeight: 700, fontSize: '13px' }}>{t(`${visuals}.walker.generateInvoice`)}</div>
            </div>
          </div>
        </div>
      );
    }
    case 'comportementaliste': {
      const behaviorNotes = t(`${visuals}.behaviorist.notes`, { returnObjects: true }) as Array<{
        animal: string;
        client: string;
        date: string;
        type: string;
        sent: boolean;
      }>;
      return (
        <div style={{ padding: '24px', background: '#fff', borderRadius: '20px', border: '1px solid #ede9fe', boxShadow: '0 10px 30px rgba(139,92,246,0.08)', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px', paddingBottom: '14px', borderBottom: '2px solid #ede9fe' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={22} color="white" />
            </div>
            <div>
              <div style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#5b21b6' }}>{t(`${visuals}.behaviorist.title`)}</div>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#7c3aed' }}>{t(`${visuals}.behaviorist.subtitle`)}</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {behaviorNotes.map((note, i) => (
              <div key={i} style={{ padding: '14px 16px', background: note.sent ? '#f5f3ff' : '#faf5ff', borderRadius: '12px', border: '1px solid #e9e5ff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>{note.animal} — {note.client}</span>
                  {note.sent ? <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600 }}>{t(`${visuals}.behaviorist.sent`)}</span> : <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 600 }}>{t(`${visuals}.behaviorist.draft`)}</span>}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>{note.type}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{note.date}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '14px', padding: '12px 16px', background: '#8b5cf6', color: 'white', borderRadius: '10px', fontSize: '13px', fontWeight: 700, textAlign: 'center' }}>{t(`${visuals}.behaviorist.writeReport`)}</div>
        </div>
      );
    }
    case 'sitter': {
      const arrivalItems = t(`${visuals}.sitter.arrivals.items`, { returnObjects: true }) as string[];
      return (
        <div style={{ padding: '28px', background: '#fff', borderRadius: '20px', border: '1px solid #e8e8e8', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', height: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div style={{ background: '#f0f5ff', borderRadius: '16px', padding: '20px', border: '1px solid #d6e4ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '40px', height: '40px', background: '#1890ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <User size={20} color="white" />
              </div>
              <div>
                <div style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>{t(`${visuals}.sitter.arrivals.title`)}</div>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>{t(`${visuals}.sitter.arrivals.count`)}</p>
              </div>
            </div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1890ff', marginBottom: '8px' }}>{t(`${visuals}.sitter.arrivals.today`)}</div>
            <div style={{ padding: '12px', background: 'white', borderRadius: '10px', marginBottom: '8px', border: '1px solid #e2e8f0' }}>{arrivalItems[0]}</div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#595959', marginBottom: '8px' }}>{t(`${visuals}.sitter.arrivals.upcoming`)}</div>
            <div style={{ padding: '12px', background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0' }}>{arrivalItems[1]}</div>
          </div>
          <div style={{ background: '#f9f0ff', borderRadius: '16px', padding: '20px', border: '1px solid #efdbff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '40px', height: '40px', background: '#722ed1', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <User size={20} color="white" />
              </div>
              <div>
                <div style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>{t(`${visuals}.sitter.departures.title`)}</div>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>{t(`${visuals}.sitter.departures.count`)}</p>
              </div>
            </div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#722ed1', marginBottom: '8px' }}>{t(`${visuals}.sitter.departures.today`)}</div>
            <div style={{ padding: '12px', background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0' }}>{t(`${visuals}.sitter.departures.item`)}</div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#595959', marginTop: '12px' }}>{t(`${visuals}.sitter.departures.upcoming`)}</div>
            <div style={{ padding: '12px', background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', marginTop: '8px', opacity: 0.7 }}>{t(`${visuals}.sitter.departures.none`)}</div>
          </div>
        </div>
      );
    }
    case 'autres': {
        const serviceTypes = t(`${visuals}.autres.types`, { returnObjects: true }) as Array<{
          title: string;
          phrase: string;
        }>;
        const serviceIcons = [<User size={24} color="#D4A017" />, <Calendar size={24} color="#1677ff" />, <FileText size={24} color="#595959" />];
        const serviceBgs = ['linear-gradient(135deg, #FFF8E7 0%, #FFF3D6 100%)', 'linear-gradient(135deg, #E6F4FF 0%, #BAE0FF 100%)', 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)'];
        return (
          <div style={{ padding: '24px', background: '#fff', borderRadius: '20px', border: '1px solid #e8e8e8', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', height: '100%' }}>
            <div style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 800, color: '#8B5A3C' }}>{t(`${visuals}.autres.title`)}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {serviceTypes.map((s, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '14px', padding: '16px', border: '1px solid #e8e8e8', borderRadius: '14px', background: '#fafafa', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: serviceBgs[i], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{serviceIcons[i]}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ margin: '0 0 6px', fontSize: '14px', fontWeight: 700, color: '#262626' }}>{s.title}</div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#595959', lineHeight: 1.45 }}>{s.phrase}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }
    default:
      return null;
  }
};

const ProfessionSwitcher = () => {
  const { t } = useTranslation('marketing');
  const { pathname } = useLocation();
  const [activeTab, setActiveTab] = useState('educateur');
  const currentMetier = buildProfessionTabs(t);

  const activeData = currentMetier.find(m => m.id === activeTab) || currentMetier[0];

  return (
    <div
      className="lp-professions"
      style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '64px', alignItems: 'center' }}
    >
      {/* Left Column: Profession Tabs (vertical list) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {currentMetier.map((m) => {
          const isActive = activeTab === m.id;
          return (
            <React.Fragment key={m.id}>
              <motion.div
                className="lp-profession-tab"
                onClick={() => setActiveTab(m.id)}
                onMouseEnter={() => {
                  if (window.matchMedia('(hover: hover)').matches) setActiveTab(m.id);
                }}
                style={{ 
                  padding: '24px 32px', 
                  borderRadius: '24px', 
                  cursor: 'pointer',
                  background: isActive ? 'white' : 'transparent',
                  border: isActive ? '1px solid rgba(165,118,92,0.1)' : '1px solid transparent',
                  boxShadow: isActive ? '0 10px 30px rgba(0,0,0,0.03)' : 'none',
                  transition: 'all 0.3s ease'
                }}
              >
                <div className="lp-profession-tab__row" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '14px', 
                    background: isActive ? m.color : '#f1f5f9',
                    color: isActive ? 'white' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    transition: 'all 0.3s ease'
                  }}>
                    {m.icon}
                  </div>
                  <div className="lp-profession-tab__copy">
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: '20px', 
                      fontWeight: 800, 
                      color: isActive ? '#1e293b' : '#64748b',
                      fontFamily: 'Plus Jakarta Sans'
                    }}>
                      {m.title}
                    </h3>
                    {isActive && (
                      <>
                        <motion.p 
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          style={{ margin: '8px 0 0 0', fontSize: '15px', color: '#64748b', lineHeight: 1.5 }}
                        >
                          {m.description}
                        </motion.p>
                        {m.to && (
                          <ProMarketingLink
                            to={localizedMetierNavPath(m.to, pathname)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              marginTop: '16px',
                              fontSize: '14px',
                              fontWeight: 700,
                              color: '#A5765C',
                              textDecoration: 'none'
                            }}
                          >
                            {t('common.cta.learnMore')} <ArrowRight size={14} />
                          </ProMarketingLink>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
              {isActive && (
                <div className="lp-professions__visual-mobile">
                  <ProfessionVisuals type={m.id} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Right Column: Visual Preview */}
      <div className="lp-professions__visual-desktop" style={{ height: '560px', position: 'relative' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20, scale: 0.98 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: "circOut" }}
            style={{ height: '100%' }}
          >
            <ProfessionVisuals type={activeTab} />
          </motion.div>
        </AnimatePresence>
        
        {/* Decorative elements */}
        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', background: `${activeData.color}10`, filter: 'blur(60px)', borderRadius: '50%', zIndex: -1 }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '200px', height: '200px', background: `${activeData.color}05`, filter: 'blur(60px)', borderRadius: '50%', zIndex: -1 }} />
      </div>
    </div>
  );
};

/* ============================
   DATA
   ============================ */


const FeatureVisual = ({ type, hoverLift = false }: { type: string; hoverLift?: boolean }) => {
  const { t } = useTranslation('marketing');
  const visuals = `${LP}.features.visuals`;
  switch (type) {
    case 'mileage':
      return (
        <div style={{ position: 'relative', height: '120px', width: '100%', background: 'rgba(165,118,92,0.03)', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(165,118,92,0.08)' }}>
          <div style={{ position: 'absolute', top: '50%', left: '10%', right: '10%', height: '2px', background: 'rgba(165,118,92,0.1)', borderRadius: '2px' }} />
          <motion.div 
            initial={{ width: 0 }}
            whileInView={{ width: '70%' }}
            transition={{ duration: 2, ease: "circOut" }}
            style={{ position: 'absolute', top: '50%', left: '10%', height: '2px', background: '#A5765C', borderRadius: '2px' }} 
          />
          <motion.div 
            initial={{ left: '10%' }}
            whileInView={{ left: '70%' }}
            transition={{ duration: 2, ease: "circOut" }}
            style={{ position: 'absolute', top: '35%', fontSize: '20px' }}>🚗</motion.div>
          <div style={{ position: 'absolute', top: '15%', right: '15%', padding: '6px 12px', background: 'white', borderRadius: '12px', fontSize: '11px', fontWeight: 900, color: '#A5765C', boxShadow: '0 10px 20px rgba(165,118,92,0.1)' }}>{t(`${visuals}.mileage.amount`)}</div>
          <div style={{ position: 'absolute', bottom: '15%', left: '15%', fontSize: '11px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t(`${visuals}.mileage.autoCalc`)}</div>
        </div>
      );
    case 'invoice':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <motion.div 
            whileHover={hoverLift ? { scale: 1.02 } : undefined}
            style={{ width: '100%', height: '50px', background: '#fff', borderRadius: '16px', border: '1px solid rgba(34, 197, 94, 0.15)', display: 'flex', alignItems: 'center', padding: '0 16px', justifyContent: 'space-between', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
             <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
               <div style={{ width: '40px', height: '6px', background: '#F1F5F9', borderRadius: '3px' }} />
               <div style={{ width: '25px', height: '4px', background: '#F1F5F9', borderRadius: '2px' }} />
             </div>
             <div style={{ padding: '4px 10px', background: '#DCFCE7', color: '#166534', fontSize: '9px', fontWeight: 950, borderRadius: '6px', letterSpacing: '0.05em' }}>{t(`${visuals}.invoice.paid`)}</div>
          </motion.div>
          <div style={{ width: '90%', height: '45px', background: '#fff', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', padding: '0 16px', opacity: 0.4 }}>
             <div style={{ width: '60px', height: '6px', background: '#F1F5F9', borderRadius: '3px' }} />
          </div>
        </div>
      );
    case 'sms':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px', width: '100%' }}>
          <motion.div 
            initial={{ x: 20, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            style={{ maxWidth: '90%', padding: '14px 18px', background: '#0EA5E9', borderRadius: '20px 20px 4px 20px', color: 'white', fontSize: '12px', fontWeight: 600, boxShadow: '0 15px 30px rgba(14, 165, 233, 0.2)', lineHeight: 1.4 }}>
            {t(`${visuals}.sms.message`)}
          </motion.div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e' }} />
            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 800 }}>{t(`${visuals}.sms.sent`)}</span>
          </div>
        </div>
      );
    case 'sync':
      return (
        <div style={{ position: 'relative', height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
          <div style={{ width: '60px', height: '60px', background: 'white', borderRadius: '18px', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
            <svg viewBox="0 0 48 48" style={{ width: '32px', height: '32px' }}>
              <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
              <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
              <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
              <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
            </svg>
          </div>
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            style={{ fontSize: '24px', color: '#A5765C' }}>🔄</motion.div>
          <div style={{ width: '60px', height: '60px', background: 'white', borderRadius: '18px', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
            <span style={{ fontSize: '24px' }}>📅</span>
          </div>
        </div>
      );
    case 'msg':
      return (
        <div style={{ position: 'relative', height: '100px', width: '100%' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '40px', height: '40px', background: '#F1F5F9', borderRadius: '12px' }} />
          <div style={{ position: 'absolute', top: 10, left: 50, right: 0, height: '10px', background: '#F1F5F9', borderRadius: '5px' }} />
          <div style={{ position: 'absolute', top: 25, left: 50, width: '60%', height: '6px', background: '#F1F5F9', borderRadius: '3px', opacity: 0.5 }} />
          <motion.div 
            style={{ position: 'absolute', bottom: 0, right: 0, width: '80%', height: '40px', background: 'white', borderRadius: '12px', border: '1px solid rgba(165,118,92,0.1)', boxShadow: '0 8px 20px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', padding: '0 12px', gap: '10px' }}>
            <span style={{ fontSize: '16px' }}>📎</span>
            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>{t(`${visuals}.msg.fileName`)}</span>
          </motion.div>
        </div>
      );
    case 'forfait':
      return (
        <div style={{ background: 'white', borderRadius: '20px', padding: '16px', border: '1px solid rgba(236, 72, 153, 0.1)', boxShadow: '0 10px 30px rgba(0,0,0,0.03)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', fontWeight: 900, color: '#334155' }}>{t(`${visuals}.forfait.name`)}</span>
            <span style={{ fontSize: '11px', fontWeight: 900, color: '#ec4899' }}>{t(`${visuals}.forfait.count`)}</span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#F1F5F9', borderRadius: '4px', overflow: 'hidden' }}>
            <motion.div 
              initial={{ width: 0 }}
              whileInView={{ width: '80%' }}
              transition={{ duration: 1.5 }}
              style={{ height: '100%', background: '#ec4899' }} />
          </div>
        </div>
      );
    case 'gift':
      return (
        <div style={{ background: 'white', borderRadius: '20px', padding: '16px', border: '1px solid rgba(219, 39, 119, 0.12)', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <span style={{ fontSize: '22px' }}>🎁</span>
            <span style={{ fontSize: '11px', fontWeight: 900, color: '#334155' }}>{t(`${visuals}.gift.name`)}</span>
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#db2777' }}>{t(`${visuals}.gift.tag`)}</div>
          <motion.div
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 2.4, repeat: Infinity }}
            style={{ position: 'absolute', right: '12px', bottom: '8px', fontSize: '28px' }}
          >
            🎀
          </motion.div>
        </div>
      );
    case 'quote':
      return (
        <div style={{ position: 'relative', height: '100px', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '16px' }}>
            <div style={{ width: '40%', height: '8px', background: '#F1F5F9', borderRadius: '4px', marginBottom: '8px' }} />
            <div style={{ width: '100%', height: '1px', background: '#E2E8F0', margin: '20px 0 10px' }} />
            <motion.div 
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              style={{ fontFamily: 'Plus Jakarta Sans', fontStyle: 'normal', color: '#A5765C', fontSize: '20px', fontWeight: 600 }}>{t(`${visuals}.quote.signature`)}</motion.div>
          </div>
          <motion.div 
            animate={{ x: [0, 5, 0], y: [0, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
            style={{ position: 'absolute', bottom: '10px', right: '10px', fontSize: '30px' }}>✍️</motion.div>
        </div>
      );
    default:
      return null;
  }
};


function useMediaQuery(query: string) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const update = () => setOn(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [query]);
  return on;
}

function useHoverLift() {
  return useMediaQuery('(hover: hover)');
}

const FeatureCard = ({ b, i }: { b: LandingBenefit; i: number }) => {
  const hoverLift = useHoverLift();
  const cardContent = (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
      whileHover={hoverLift ? { y: -12, transition: { duration: 0.4, ease: 'easeOut' } } : undefined}
      className="lp-feature-card"
      style={{
        padding: '56px 48px',
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(32px)',
        borderRadius: '48px',
        border: '1px solid rgba(165,118,92,0.15)',
        boxShadow: `
          0 4px 6px -1px rgba(0, 0, 0, 0.02),
          0 20px 40px -8px rgba(165,118,92,0.08),
          0 40px 80px -12px rgba(0, 0, 0, 0.04)
        `,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        cursor: b.to ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)'
      }}
      onMouseEnter={(e) => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        e.currentTarget.style.borderColor = 'rgba(165,118,92,0.4)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.95)';
        e.currentTarget.style.boxShadow = `
          0 8px 12px -2px rgba(0, 0, 0, 0.03),
          0 30px 60px -12px rgba(165,118,92,0.15),
          0 60px 120px -15px rgba(0, 0, 0, 0.06)
        `;
      }}
      onMouseLeave={(e) => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        e.currentTarget.style.borderColor = 'rgba(165,118,92,0.15)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.7)';
        e.currentTarget.style.boxShadow = `
          0 4px 6px -1px rgba(0, 0, 0, 0.02),
          0 20px 40px -8px rgba(165,118,92,0.08),
          0 40px 80px -12px rgba(0, 0, 0, 0.04)
        `;
      }}
    >
      {/* Gloss Effect Line */}
      <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)', zIndex: 2 }} />
      {/* Dynamic Background Accent */}
      <div style={{ 
        position: 'absolute', 
        top: '-10%', 
        right: '-10%', 
        width: '200px', 
        height: '200px', 
        background: `radial-gradient(circle, ${b.color}10 0%, transparent 70%)`,
        zIndex: 0
      }} />

      <div style={{ zIndex: 1 }}>
        <motion.div 
          whileHover={hoverLift ? { rotate: [0, -10, 10, 0], scale: 1.1 } : undefined}
          style={{ 
            fontSize: '32px', 
            background: 'white', 
            width: '64px', 
            height: '64px', 
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '32px',
            boxShadow: '0 12px 24px rgba(0,0,0,0.05)',
            border: '1px solid rgba(0,0,0,0.03)'
          }}>
          {b.icon}
        </motion.div>
        <h3 style={{ 
          fontSize: '24px', 
          fontWeight: 900, 
          color: '#1e293b', 
          marginBottom: '16px', 
          letterSpacing: '-0.03em',
          fontFamily: 'Plus Jakarta Sans'
        }}>
          {b.title}
        </h3>
        <p style={{ 
          fontSize: '16px', 
          color: '#64748b', 
          lineHeight: 1.6, 
          fontWeight: 500, 
          marginBottom: '32px' 
        }}>
          {b.description}
        </p>
      </div>

      <motion.div 
        initial={{ opacity: 0.8 }}
        whileHover={hoverLift ? { opacity: 1, scale: 1.02 } : undefined}
        style={{ zIndex: 1, marginTop: 'auto' }}>
        <FeatureVisual type={b.id} hoverLift={hoverLift} />
      </motion.div>
    </motion.div>
  );

  if (b.to) {
    return (
      <ProMarketingLink
        to={b.to}
        style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}
      >
        {cardContent}
      </ProMarketingLink>
    );
  }
  return cardContent;
};


/* ============================
   MOBILE APPS SECTION
   ============================ */

/* Contenu écran planning pour le mockup téléphone */
const MobilePlanningScreen = ({ notifications = [] }: { notifications?: any[] }) => {
  const { t } = useTranslation('marketing');
  const mockup = `${LP}.mobile.mockup`;
  const mockupEvents = t(`${mockup}.events`, { returnObjects: true }) as Array<{ label: string; client: string }>;
  const hours = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
  const currentTime = "10:20";
  
  const events = [
    { start: "09:00", end: "10:30", label: mockupEvents[0]?.label ?? '', client: mockupEvents[0]?.client ?? '', color: "#38bdf8" },
    { start: "11:00", end: "12:00", label: mockupEvents[1]?.label ?? '', client: mockupEvents[1]?.client ?? '', color: "#A5765C" },
    { start: "14:30", end: "16:00", label: mockupEvents[2]?.label ?? '', client: mockupEvents[2]?.client ?? '', color: "#52c41a" },
  ];

  const getTop = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    const startHour = 8;
    return ((h - startHour) * 60 + m) * 1.2; // 1.2px per minute
  };

  const getHeight = (start: string, end: string) => {
    const [h1, m1] = start.split(':').map(Number);
    const [h2, m2] = end.split(':').map(Number);
    return ((h2 - h1) * 60 + (m2 - m1)) * 1.2;
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fff', position: 'relative', overflow: 'hidden' }}>
      {/* Floating Notifications */}
      <div style={{ position: 'absolute', top: '50px', left: '12px', right: '12px', zIndex: 100, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <AnimatePresence mode="popLayout">
          {notifications.map((item) => (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, scale: 0.9, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
              style={{
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(12px)',
                padding: '12px 16px',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
                border: '1px solid rgba(0,0,0,0.03)'
              }}
            >
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: item.iconBg, color: item.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <item.Icon size={18} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: 850, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</div>
                <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 500 }}>{item.sub}</div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* iOS Status Bar */}
      <div style={{ padding: '8px 20px', fontSize: '12px', color: '#000', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, background: '#fff' }}>
        <span>9:41</span>
        <div style={{ display: 'flex', gap: '5px' }}>
          <div style={{ width: '16px', height: '10px', border: '1px solid #000', borderRadius: '2px', position: 'relative' }}>
            <div style={{ position: 'absolute', left: '2px', top: '2px', bottom: '2px', right: '4px', background: '#000' }} />
          </div>
        </div>
      </div>

      {/* Header Planning */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ fontWeight: 900, fontSize: '20px', color: '#101828', letterSpacing: '-0.02em' }}>{t(`${mockup}.planning`)}</div>
        <div style={{ fontSize: '13px', color: '#A5765C', fontWeight: 750, marginTop: '2px' }}>{t(`${mockup}.today`)}</div>
      </div>

      {/* Day Schedule View */}
      <div style={{ flex: 1, overflowY: 'auto', position: 'relative', padding: '0 0 20px 0' }} className="lp-mockup-scroll">
        <div style={{ position: 'relative', height: `${(18-8+1)*60*1.2}px` }}>
          {/* Hour Guideline Gutter */}
          {hours.map(h => (
            <div key={h} style={{ display: 'flex', alignItems: 'flex-start', height: `${60*1.2}px`, borderTop: '1px solid #f1f5f9' }}>
              <span style={{ width: '50px', textAlign: 'right', paddingRight: '12px', fontSize: '10px', color: '#94a3b8', fontWeight: 600, marginTop: '-6px' }}>{h}:00</span>
              <div style={{ flex: 1 }} />
            </div>
          ))}

          {/* Time Indicator Now */}
          <div style={{ position: 'absolute', top: `${getTop(currentTime)}px`, left: '50px', right: 0, display: 'flex', alignItems: 'center', zIndex: 10 }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ff4d4f', marginLeft: '-4px' }} />
            <div style={{ flex: 1, height: '2px', background: '#ff4d4f' }} />
          </div>

          {/* Real Events Blocs */}
          {events.map((ev, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: `${getTop(ev.start)}px`,
                left: '60px',
                right: '12px',
                height: `${getHeight(ev.start, ev.end)}px`,
                background: `${ev.color}15`,
                borderLeft: `3px solid ${ev.color}`,
                borderRadius: '8px',
                padding: '8px 12px',
                overflow: 'hidden',
                transition: 'transform 0.3s ease'
              }}
            >
              <div style={{ fontWeight: 850, fontSize: '12px', color: '#101828' }}>{ev.label}</div>
              <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>{ev.client}</div>
              <div style={{ position: 'absolute', bottom: '8px', right: '8px', fontSize: '9px', fontWeight: 800, color: ev.color, background: '#fff', padding: '2px 6px', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                {ev.start}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Nav App Style */}
      <div style={{ height: '70px', background: '#fff', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-around', alignItems: 'center', paddingBottom: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: '#A5765C' }}>
          <Calendar size={20} />
          <span style={{ fontSize: '10px', fontWeight: 800 }}>{t(`${mockup}.nav.agenda`)}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: '#94a3b8' }}>
          <User size={20} />
          <span style={{ fontSize: '10px', fontWeight: 650 }}>{t(`${mockup}.nav.clients`)}</span>
        </div>
        <div style={{ 
          width: '44px', 
          height: '44px', 
          borderRadius: '14px', 
          background: '#A5765C', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: '#fff',
          boxShadow: '0 8px 16px rgba(165,118,92,0.3)',
          marginTop: '-40px'
        }}>
          <Plus size={24} strokeWidth={3} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: '#94a3b8' }}>
          <CreditCard size={20} />
          <span style={{ fontSize: '10px', fontWeight: 650 }}>{t(`${mockup}.nav.sales`)}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: '#94a3b8' }}>
          <Ellipsis size={20} />
          <span style={{ fontSize: '10px', fontWeight: 650 }}>{t(`${mockup}.nav.more`)}</span>
        </div>
      </div>
    </div>
  );
};

const MobileAppsSection = () => {
  const { t } = useTranslation('marketing');
  const mobile = `${LP}.mobile`;
  const heroNotificationsPool = useHeroNotificationsPool();
  const [activePanel, setActivePanel] = useState<'apple' | 'android' | null>('apple');
  
  const heroNotificationKeyRef = useRef(0);
  type MobileNotificationItem = (typeof heroNotificationsPool)[number] & { key: number };
  
  const pickNotification = (visibleIds: string[]) => {
    const available = heroNotificationsPool.filter((t) => !visibleIds.includes(t.id));
    const pool = available.length > 0 ? available : heroNotificationsPool;
    const type = pool[Math.floor(Math.random() * pool.length)];
    return { ...type, key: heroNotificationKeyRef.current++ } as MobileNotificationItem;
  };
  
  const [mobileNotifications, setMobileNotifications] = useState<MobileNotificationItem[]>([]);

  useEffect(() => {
    const addOne = () => {
      setMobileNotifications((prev) => {
        const next = [...prev];
        const visibleIds = next.map((n) => n.id);
        if (next.length >= 2) next.shift();
        next.push(pickNotification(visibleIds));
        return next;
      });
    };
    const firstArrival = setTimeout(addOne, 2000);
    const interval = setInterval(addOne, 4000);
    return () => {
      clearTimeout(firstArrival);
      clearInterval(interval);
    };
  }, []);

  return (
    <section id="mobile" className="lp-mobile-section">
      <div className="lp-mobile-inner">
        <div className="lp-mobile-heading">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <h2 className="lp-section-title" style={{ fontSize: 'clamp(40px, 8vw, 96px)', color: '#fff', lineHeight: 0.9, marginBottom: '24px' }}>
              {t(`${mobile}.titleLine1`)} <br />
              <span style={{ 
                fontFamily: 'Plus Jakarta Sans', 
                fontWeight: 900, 
                color: '#A5765C',
                textShadow: '0 0 40px rgba(165,118,92,0.3)'
              }}>
                {t(`${mobile}.titleLine2`)}<span className="lp-title-dot" aria-hidden="true" />
              </span>
            </h2>
            <p className="lp-section-subtitle" style={{ maxWidth: '700px', margin: '0 auto', color: '#94a3b8', fontSize: '22px', fontWeight: 500 }}>
              {t(`${mobile}.subtitle`)}
            </p>
          </motion.div>
        </div>

        <div className="lp-mobile-grid">
          {/* --- 3D Mockup Block --- */}
          <motion.div 
            className="lp-mobile-phone-container"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="lp-mobile-phone-3d">
              <div className="lp-mobile-phone-screen-3d">
                <MobilePlanningScreen notifications={mobileNotifications} />
              </div>
            </div>
          </motion.div>

          {/* --- Store Integration Block --- */}
          <div className="lp-mobile-portal-stack">
            {/* Apple Card */}
            <div 
              className={`lp-store-panel ${activePanel === 'apple' ? 'lp-store-panel--active' : ''}`}
              onClick={() => setActivePanel('apple')}
              onMouseEnter={() => {
                if (window.matchMedia('(hover: hover)').matches) setActivePanel('apple');
              }}
            >
              <div className="lp-store-panel__header">
                <div className="lp-store-panel__icon" style={{ 
                  background: activePanel === 'apple' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)',
                  boxShadow: activePanel === 'apple' ? '0 0 20px rgba(255,255,255,0.2)' : 'none'
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.96.95-2.22 1.6-3.41 1.6-1.21 0-1.99-.44-3.04-.44-1.07 0-2.03.45-3.05.45-1.4 0-2.92-1-3.95-2.58C1.86 16.59.5 12.33 2.25 9.3c.87-1.51 2.42-2.47 4.12-2.47 1.29 0 2.5.89 3.29.89.78 0 2.26-.89 3.8-.89 1.6 0 3.1.92 4.04 2.22-3.23 1.34-2.73 6.08.45 7.23-.62 1.54-1.6 3.1-2.9 4zm-3.41-15.63c-.04-.01-.08-.01-.12-.01-.08 0-.15.01-.23.01.07-2.07 1.83-3.65 3.52-3.65.04.01.08.01.12.01.08 0 .15-.01.23-.01-1.12 3-3.52 3.64-3.52 3.64z"/>
                  </svg>
                </div>
                <div className="lp-store-panel__info">
                  <div className="lp-store-panel__eyebrow">{t(`${mobile}.apple.eyebrow`)}</div>
                  <p>{t(`${mobile}.apple.store`)}</p>
                </div>
                <div className="lp-store-panel__toggle">
                  <ArrowRight size={24} style={{ transform: activePanel === 'apple' ? 'rotate(90deg)' : 'none' }} />
                </div>
              </div>
              
              <AnimatePresence>
                {activePanel === 'apple' && (
                  <motion.div 
                    className="lp-store-panel__content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <div className="lp-qr-container">
                      <div className="lp-qr-box">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent('https://apps.apple.com/fr/app/rintintin-pro/id6754002576')}`}
                          alt="QR App Store"
                          height={160}
                        />
                      </div>
                    </div>
                    <div className="lp-qr-instructions">
                      <div className="lp-qr-instructions__title">{t(`${mobile}.apple.installTitle`)}</div>
                      <p>{t(`${mobile}.apple.installBody`)}</p>
                      <a href="https://apps.apple.com/fr/app/rintintin-pro/id6754002576" target="_blank" rel="noreferrer" className="lp-direct-btn">
                        {t(`${mobile}.apple.link`)} <ArrowRight size={20} />
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Android Card */}
            <div 
              className={`lp-store-panel ${activePanel === 'android' ? 'lp-store-panel--active' : ''}`}
              onClick={() => setActivePanel('android')}
              onMouseEnter={() => {
                if (window.matchMedia('(hover: hover)').matches) setActivePanel('android');
              }}
            >
              <div className="lp-store-panel__header">
                <div className="lp-store-panel__icon" style={{ 
                                    background: activePanel === 'android' ? 'rgba(56,189,248,0.1)' : 'rgba(255,255,255,0.05)',
                                    borderColor: activePanel === 'android' ? 'rgba(56,189,248,0.3)' : 'rgba(255,255,255,0.1)',
                                    color: activePanel === 'android' ? '#38bdf8' : '#fff'
                                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.523 15.3414L20.355 20.2114C20.535 20.5214 20.435 20.9114 20.125 21.0914C19.815 21.2714 19.425 21.1714 19.245 20.8614L16.395 15.9614C14.855 16.6314 13.065 17.0114 11.2 17.0114C9.335 17.0114 7.545 16.6314 6.005 15.9614L3.155 20.8614C2.975 21.1714 2.585 21.2714 2.275 21.0914C1.965 20.9114 1.865 20.5214 2.045 20.2114L4.877 15.3414C2.107 13.6814 0.205 10.7714 0.205 7.42142V6.66142H22.195V7.42142C22.195 10.7714 20.293 13.6814 17.523 15.3414ZM6.445 11.0114C7.10774 11.0114 7.645 10.4742 7.645 9.81142C7.645 9.14868 7.10774 8.61142 6.445 8.61142C5.78226 8.61142 5.245 9.14868 5.245 9.81142C5.245 10.4742 5.78226 11.0114 6.445 11.0114ZM15.945 11.0114C16.6077 11.0114 17.145 10.4742 17.145 9.81142C17.145 9.14868 16.6077 8.61142 15.945 8.61142C15.2823 8.61142 14.745 9.14868 14.745 9.81142C14.745 10.4742 15.2823 11.0114 15.945 11.0114ZM11.2 0.0114227C11.5314 0.0114227 11.8 0.280053 11.8 0.611423V3.61142C11.8 3.94279 11.5314 4.21142 11.2 4.21142C10.8686 4.21142 10.6 3.94279 10.6 3.61142V0.611423C10.6 0.280053 10.8686 0.0114227 11.2 0.0114227Z"/>
                  </svg>
                </div>
                <div className="lp-store-panel__info">
                  <div className="lp-store-panel__eyebrow">{t(`${mobile}.android.eyebrow`)}</div>
                  <p>{t(`${mobile}.android.store`)}</p>
                </div>
                <div className="lp-store-panel__toggle">
                  <ArrowRight size={24} style={{ transform: activePanel === 'android' ? 'rotate(90deg)' : 'none' }} />
                </div>
              </div>
              
              <AnimatePresence>
                {activePanel === 'android' && (
                  <motion.div 
                    className="lp-store-panel__content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <div className="lp-qr-container">
                      <div className="lp-qr-box">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent('https://play.google.com/store/apps/details?id=io.rintintin.app&hl=fr')}`}
                          alt="QR Google Play"
                          height={160}
                        />
                      </div>
                    </div>
                    <div className="lp-qr-instructions">
                      <div className="lp-qr-instructions__title">{t(`${mobile}.android.installTitle`)}</div>
                      <p>{t(`${mobile}.android.installBody`)}</p>
                      <a href="https://play.google.com/store/apps/details?id=io.rintintin.app&hl=fr" target="_blank" rel="noreferrer" className="lp-direct-btn">
                        {t(`${mobile}.android.link`)} <ArrowRight size={20} />
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================
   COMPONENT
   ============================ */

/** #1120 v4 : clé de présence liée au jeu de mots (donc à la langue). */
export function heroActionWordsKey(words: unknown): string {
  return Array.isArray(words) ? words.join('|') : String(words ?? '');
}

export default function LandingPageV2() {
  const { i18n } = useTranslation('marketing');
  const location = useLocation();
  const pathLocale =
    getLocaleFromPath(location.pathname) ?? marketingLocaleFromLanguage(i18n.language);
  const t = React.useMemo(
    () => i18n.getFixedT(pathLocale, 'marketing'),
    [i18n, pathLocale]
  );
  const [cinemaMode, setCinemaMode] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const hoverLift = useHoverLift();
  const compactHero = useMediaQuery('(max-width: 992px)');

  const actionWords = t(`${LP}.hero.actionWords`, { returnObjects: true }) as string[];
  const heroWordsKey = heroActionWordsKey(actionWords);
  const benefits = buildLandingBenefits(t);
  const faqItems = buildLandingFaqItems(t);
  const canonicalPath = MARKETING_PATHS.home[pathLocale];

  // Desktop only: PublicLayout gère les ancres ≤1024 (retry lazy + layout).
  useEffect(() => {
    const hash = location.hash?.slice(1);
    if (!hash) return undefined;
    const scrollToSection = () => {
      if (window.matchMedia('(max-width: 1024px)').matches) return;
      const el = document.getElementById(hash);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const scrollTimer = setTimeout(scrollToSection, 100);
    return () => clearTimeout(scrollTimer);
  }, [location.pathname, location.hash]);

  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    if (!actionWords.length) return undefined;
    const wordTimer = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % actionWords.length);
    }, 3000);
    return () => clearInterval(wordTimer);
  }, [actionWords.length]);

  const schemas = [
    buildWebSiteSchema(),
    buildOrganizationSchema(),
    buildSoftwareApplicationSchema({
      featureList: getLandingSchemaFeatures(t),
    }),
    buildFAQPageSchema(faqItems),
    buildBreadcrumbSchema([{ name: t(`${LP}.seo.breadcrumbHome`), path: canonicalPath }]),
    buildAggregateRatingSchema({
      itemName: 'Rintintin Pro',
      ratingValue: 4.4,
      ratingCount: 10,
      reviewUrl: 'https://fr.trustpilot.com/review/rintintin-pro.com',
    }),
  ];

  return (
    <>
      <SEOHead
        title={t(`${LP}.seo.title`)}
        description={t(`${LP}.seo.description`)}
        canonicalPath={canonicalPath}
        alternatePaths={marketingHreflangCluster('home')}
        keywords={t(`${LP}.seo.keywords`)}
        schemaData={schemas}
      />

      {/* ===== HERO ===== */}
      <section className="lp-hero lp-hero--under-fixed-nav">
        {/* Glow qui suit la souris (optionnel mais wow) */}
        <motion.div 
          className="lp-hero__mouse-glow"
          style={{
            position: 'absolute',
            width: '800px',
            height: '800px',
            background: 'radial-gradient(circle, rgba(255, 184, 76, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
            zIndex: 1,
          }}
          // On peut animer ça via un hook de tracking souris si on veut aller loin,
          // mais ici on va juste mettre un glow fixe doux.
        />

        <div className="lp-hero__inner">
          <div className="lp-hero__content">
            <motion.div
              className="lp-hero__badge"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t(`${LP}.hero.badge`)}
            </motion.div>

            <h1
              className="lp-hero__title"
              style={{ lineHeight: 1.05, letterSpacing: '-0.04em' }}
            >
              <div style={{ display: 'block' }}>
                <span
                  className="lp-hero__action-slot"
                  style={{ position: 'relative', display: 'inline-block', height: '1.2em', verticalAlign: 'bottom', overflow: 'hidden' }}
                >
                  {compactHero ? (
                    <>
                      <span className="lp-hero__action-sizer" aria-hidden>
                        {actionWords[wordIndex]}
                        <span className="lp-title-dot" />
                      </span>
                      {/* #1120 v4 : remonte la présence au changement de langue (pas de mot FR qui sort pendant l’arrivée du texte EN) */}
                      <AnimatePresence initial={false} key={heroWordsKey}>
                        <motion.span
                          key={actionWords[wordIndex]}
                          initial={{ opacity: 0, y: '110%' }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: '-110%' }}
                          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            display: 'inline-block',
                            color: '#E6A23C',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {actionWords[wordIndex]}<span className="lp-title-dot" aria-hidden="true" />
                        </motion.span>
                      </AnimatePresence>
                    </>
                  ) : (
                    <AnimatePresence mode="wait" key={heroWordsKey}>
                      <motion.span
                        key={actionWords[wordIndex]}
                        initial={{ opacity: 0, y: '100%' }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: '-100%' }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        style={{
                          display: 'inline-block',
                          color: '#E6A23C'
                        }}
                      >
                        {actionWords[wordIndex]}<span className="lp-title-dot" aria-hidden="true" />
                      </motion.span>
                    </AnimatePresence>
                  )}
                </span>
                <span style={{ display: 'block', marginTop: '10px', color: '#ffffff' }}>{t(`${LP}.hero.titleRest`)}<span className="lp-title-dot" /></span>
              </div>
            </h1>

            <motion.p
              className="lp-hero__subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              style={{ fontSize: '26px', fontWeight: 600, color: '#94a3b8', marginTop: '10px' }}
            >
              {t(`${LP}.hero.subtitle`)}
            </motion.p>

            <motion.div
              className="lp-hero__cta-row"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <MarketingCTA
                label={t(`${LP}.cta.startFree`)}
                variant="primary"
                size="xl"
                trackEvent="Hero CTA"
              />
              <MarketingCTA
                label={t(`${LP}.cta.seeHowItWorks`)}
                variant="outline"
                size="xl"
                onClick={() => setDemoOpen(true)}
                style={{ 
                  color: '#ffffff', 
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)'
                }}
              />
            </motion.div>

            <motion.div
              className="lp-hero__trustbar"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <TrustBar variant="inline" />
            </motion.div>
          </div>

          {!compactHero && (
          <div className="lp-hero__visual">
            <AppShowcaseVisual />
          </div>
          )}
        </div>
      </section>

      {/* ===== PARTNERS — teaser scroll : seul le titre dépasse en bas du fold ===== */}
      <MarketingPartnersStrip className="lp-partners lp-partners--scroll-teaser" labelKey="landingV2.partners.label" />

      <MarketingWorkflow />

      {/* ===== OFFRE ACACED (FR uniquement, auto-masquée hors pages éligibles) ===== */}
      <AcacedTeaser placement="landing" />

      {/* ===== FEATURES TOOLKIT (HYPER-PREMIUM) ===== */}
      <section className="lp-features" id="fonctionnalites" style={{ padding: '160px 24px', background: '#fafaf9', position: 'relative', overflow: 'hidden' }}>
        {/* Subtle Background Elements */}
        <div style={{ position: 'absolute', top: '10%', right: '-5%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(165,118,92,0.05) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div style={{ position: 'absolute', bottom: '10%', left: '-5%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(155,89,182,0.05) 0%, transparent 70%)', filter: 'blur(60px)' }} />

        <div style={{ maxWidth: '1300px', margin: '0 auto', position: 'relative' }}>
          <div className="lp-features__intro" style={{ textAlign: 'center', marginBottom: '120px', position: 'relative' }}>
            {/* Soft Ambient Glow */}
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '120%', height: '120%', background: 'radial-gradient(circle, rgba(165,118,92,0.08) 0%, transparent 70%)', zIndex: 0, pointerEvents: 'none' }} />

            {/* Premium Badge */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '40px' }}>
              <motion.div
                className="lp-features__badge"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 24px',
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '100px',
                  border: '1px solid rgba(165,118,92,0.15)',
                  color: '#A5765C',
                  fontSize: '11px',
                  fontWeight: 900,
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                  boxShadow: '0 15px 35px rgba(165,118,92,0.1)',
                  position: 'relative',
                  zIndex: 2
                }}
              >
                <motion.div
                   animate={{ rotate: [0, 15, -15, 0] }}
                   transition={{ duration: 3, repeat: Infinity }}
                >
                  <Sparkles size={14} fill="#A5765C" />
                </motion.div>
                {t(`${LP}.features.badge`)}
              </motion.div>
            </div>

            {/* Main Headline */}
            <div className="lp-features__heading" style={{ position: 'relative', display: 'inline-block' }}>
              <h2 style={{ 
                fontSize: 'clamp(44px, 7.5vw, 88px)', 
                fontWeight: 900, 
                color: '#1e293b', 
                marginBottom: '16px',
                fontFamily: 'Plus Jakarta Sans',
                letterSpacing: '-0.06em',
                lineHeight: 0.9,
                position: 'relative',
                zIndex: 2
              }}>
                <motion.span
                  initial={{ opacity: 0, filter: 'blur(12px)', y: 30 }}
                  whileInView={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                  style={{ display: 'block' }}
                >
                  {t(`${LP}.features.titleLine1`)}
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, filter: 'blur(12px)', y: 30 }}
                  whileInView={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  style={{ 
                    color: '#A5765C', 
                    fontFamily: 'Plus Jakarta Sans', 
                    fontStyle: 'normal', 
                    fontWeight: 800,
                    display: 'block',
                    position: 'relative',
                    letterSpacing: '-0.02em',
                    marginTop: '4px'
                  }}
                >
                  {t(`${LP}.features.titleLine2`)}<span className="lp-title-dot" aria-hidden="true" />
                </motion.span>
              </h2>
              
              {/* Animated Floating Assets */}
              <motion.div
                className="lp-features__float"
                animate={{ 
                  y: [0, -15, 0],
                  rotate: [0, 10, 0],
                  opacity: [0.4, 0.8, 0.4]
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                style={{
                  position: 'absolute',
                  top: '-30px',
                  right: '-50px',
                  fontSize: '48px',
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 0 20px rgba(165,118,92,0.3))'
                }}
              >
                ✨
              </motion.div>
              <motion.div
                className="lp-features__float"
                animate={{ 
                  y: [0, 15, 0],
                  rotate: [0, -10, 0],
                  opacity: [0.2, 0.5, 0.2]
                }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                style={{
                  position: 'absolute',
                  bottom: '-20px',
                  left: '-60px',
                  fontSize: '32px',
                  pointerEvents: 'none'
                }}
              >
                🪄
              </motion.div>
            </div>
          </div>

          <div
            className="lp-features__grid lp-features__grid--lg"
            style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', 
            gap: '32px' 
          }}>
            {benefits.slice(0, 3).map((b, i) => (
              <FeatureCard key={i} b={b} i={i} />
            ))}
          </div>

          <div
            className="lp-features__grid lp-features__grid--sm"
            style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '32px',
            marginTop: '32px'
          }}>
            {benefits.slice(3).map((b, i) => (
              <FeatureCard key={i} b={b} i={i + 3} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== FOUNDER & VIDEO DEMO (HUMAN TRUST) ===== */}
      <section
        className="lp-founders"
        style={{ padding: '120px 24px', background: '#fff', position: 'relative' }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div
            className="lp-founders__grid"
            style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) 1.2fr', gap: '80px', alignItems: 'center' }}
          >
            
            {/* Left: Founder Note */}
            <motion.div
              className="lp-founders__copy"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div style={{ 
                width: '40px', 
                height: '3px', 
                background: '#A5765C', 
                marginBottom: '32px',
                borderRadius: '2px'
              }} />
              
              <h2 style={{ 
                fontFamily: 'Plus Jakarta Sans', 
                fontSize: '32px', 
                fontWeight: 900, 
                color: '#1e293b', 
                marginBottom: '28px',
                lineHeight: 1.2,
                letterSpacing: '-0.03em'
              }}>
                {t(`${LP}.founder.title`)} <span style={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, color: '#A5765C' }}>{t(`${LP}.founder.titleAccent`)}</span>
              </h2>
              
              <p
                className="lp-founders__quote"
                style={{ 
                fontFamily: 'Plus Jakarta Sans', 
                fontStyle: 'normal', 
                fontSize: '24px', 
                color: '#A5765C', 
                lineHeight: 1.5,
                marginBottom: '32px',
                position: 'relative',
                paddingLeft: '24px',
                borderLeft: '1px solid rgba(165,118,92,0.2)',
                fontWeight: 500
              }}>
                &ldquo;{t(`${LP}.founder.quote`)}&rdquo;
              </p>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ 
                  width: '56px', 
                  height: '56px', 
                  borderRadius: '16px', 
                  background: '#f1f5f9', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>
                  🤝
                </div>
                <div>
                  <div style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#1e293b' }}>{t('common.founder.namesShort')}</div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#64748b', fontWeight: 600 }}>{t('common.founder.titleShort')}</p>
                </div>
              </div>
            </motion.div>

            {/* Right: Video Demo */}
            <motion.div
              className="lp-founders__video"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ position: 'relative' }}
            >
              {/* Playful background element */}
              <div className="lp-founders__video-glow" style={{ position: 'absolute', top: '-30px', right: '-30px', width: '100%', height: '100%', border: '2px solid rgba(165,118,92,0.1)', borderRadius: '40px', zIndex: 0 }} />
              
              <div style={{ 
                position: 'relative', 
                zIndex: 1, 
                borderRadius: '32px', 
                overflow: 'hidden', 
                aspectRatio: '16/9',
                boxShadow: '0 40px 100px rgba(0,0,0,0.15)',
                background: '#000',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                {/* Play Overlay */}
                <div 
                  className="video-play-overlay"
                  onClick={() => setCinemaMode(true)}
                >
                  <div className="video-play-button">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 3L19 12L5 21V3Z" fill="#1e293b" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>

                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/aDFaLWhfv7o?rel=0&modestbranding=1"
                  title={t(`${LP}.founder.videoTitle`)}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ borderRadius: '32px' }}
                ></iframe>
              </div>
              
              {/* Video Badge */}
              <div
                className="lp-founders__badge"
                style={{ 
                  position: 'absolute', 
                  bottom: '-24px', 
                  right: '40px', 
                  background: 'white', 
                  padding: '12px 24px', 
                  borderRadius: '100px', 
                  boxShadow: '0 15px 30px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  zIndex: 2
                }}
              >
                <div style={{ width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%', animation: 'pulse 2s infinite' }} />
                <span style={{ fontSize: '13px', fontWeight: 900, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t(`${LP}.founder.demoBadge`)}
                </span>
              </div>
            </motion.div>

          </div>
        </div>
        
        <style>{`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.5); opacity: 0.5; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </section>

            <LandingWallOfLove />

      {/* ===== GUIDES PRO (maillage blog) ===== */}
      <section
        className="lp-blog-guides"
        aria-labelledby="lp-blog-guides-heading"
        style={{ padding: '120px 24px', background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.04)' }}
      >
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              style={{
                fontSize: '14px',
                fontWeight: 800,
                color: '#A5765C',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                display: 'block',
                marginBottom: '16px',
              }}
            >
              {t(`${LP}.blogGuides.eyebrow`)}
            </motion.span>
            <h2 id="lp-blog-guides-heading" className="lp-section-title" style={{ fontSize: 'clamp(32px, 5vw, 48px)', marginBottom: '16px' }}>
              {t(`${LP}.blogGuides.title`)}
            </h2>
            <p className="lp-section-subtitle" style={{ maxWidth: '560px', margin: '0 auto' }}>
              {t(`${LP}.blogGuides.subtitle`)}
            </p>
          </div>
          <div
            className="lp-blog-guides__grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: '20px',
            }}
          >
            {LANDING_TOP_BLOG_GUIDES.map((guide, i) => (
              <motion.div
                key={guide.href}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
              >
                <ProMarketingLink
                  className="lp-blog-guides__card"
                  to={guide.href}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    height: '100%',
                    padding: '22px 20px',
                    borderRadius: '16px',
                    border: '1px solid rgba(165,118,92,0.14)',
                    background: '#fafaf9',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: '16px', lineHeight: 1.35, color: '#1e293b' }}>
                    {guide.label}
                  </span>
                  <span style={{ fontSize: '14px', lineHeight: 1.5, color: '#64748b', flex: 1 }}>
                    {guide.teaser}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#A5765C' }}>{t(`${LP}.blogGuides.readMore`)}</span>
                </ProMarketingLink>
              </motion.div>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: '32px' }}>
            <ProMarketingLink className="lp-blog-guides__all" to="/blog" style={{ color: '#A5765C', fontWeight: 600, textDecoration: 'none' }}>
              {t(`${LP}.blogGuides.viewAll`)}
            </ProMarketingLink>
          </p>
        </div>
      </section>

      {/* ===== MÉTIERS (PROFESSION SWITCHER) ===== */}
      <section className="lp-professions-section" style={{ padding: '160px 24px', background: '#fafaf9', position: 'relative', overflowX: 'clip' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div className="lp-professions__intro" style={{ textAlign: 'center', marginBottom: '80px' }}>
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              style={{ fontSize: '14px', fontWeight: 800, color: '#A5765C', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: '16px' }}
            >
              {t(`${LP}.professions.eyebrow`)}
            </motion.span>
            <h2 className="lp-section-title" style={{ fontSize: '56px', marginBottom: '24px' }}>
              {t(`${LP}.professions.title`)} <span style={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, color: '#A5765C' }}>{t(`${LP}.professions.titleAccent`)}<span className="lp-title-dot" aria-hidden="true" /></span>
            </h2>
            <p className="lp-section-subtitle" style={{ maxWidth: '600px', margin: '0 auto' }}>
              {t(`${LP}.professions.subtitle`)}
            </p>
          </div>

          <ProfessionSwitcher />
        </div>
      </section>

      {/* ===== CTA BANNER ===== */}
      <section className="lp-cta-banner">
        <motion.div 
          className="lp-cta-banner__inner"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="lp-cta-banner__title">{t(`${LP}.ctaBanner.title`)}</h2>
          <p className="lp-cta-banner__sub">{t(`${LP}.ctaBanner.subtitle`)}</p>
          <MarketingCTA
            label={t(`${LP}.cta.startFree`)}
            variant="primary"
            size="xl"
            trackEvent="Mid Page CTA"
            style={{ 
              background: '#fff', 
              color: '#1e293b', 
              borderRadius: '100px', 
              fontSize: '18px', 
              fontWeight: 800,
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
            }}
          />
        </motion.div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="tarifs" className="lp-pricing-section" style={{ padding: '120px 24px', background: '#fff' }}>
        <PricingSection />
      </section>

      <ConfigurationOfferSection onCTAClick={() => {}} />

      {/* ===== FAQ ===== (JSON-LD injecté dans le head via buildFAQPageSchema) */}
      <section id="faq" className="lp-faq-cta" style={{ padding: '120px 24px', background: '#fafaf9', borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
        <FAQSection items={faqItems} />
        <div style={{ textAlign: 'center', marginTop: 48 }}>
          <p className="lp-faq-cta__links" style={{ marginBottom: 24, fontSize: 16 }}>
            <ProMarketingLink to="/faq">Toutes les FAQ</ProMarketingLink>
            {' · '}
            <ProMarketingLink to="/avis-rintintin-pro">Avis & preuves</ProMarketingLink>
          </p>
          <MarketingCTA
            label={t('common.cta.tryFree')}
            variant="outline"
            size="lg"
            trackEvent="FAQ CTA"
            style={{ borderRadius: '100px', borderColor: '#A5765C', color: '#A5765C' }}
          />
        </div>
      </section>

      {/* ===== MOBILE APPS ===== */}
      <MobileAppsSection />

      {/* ===== CONTACT ===== */}
      <section id="contact" className="lp-contact" style={{ padding: '160px 24px', background: '#fff', position: 'relative' }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(165,118,92,0.05) 0%, transparent 70%)', filter: 'blur(100px)', zIndex: 0 }} />
        
        <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <div style={{ marginBottom: '48px' }}>
            <h2 className="lp-section-title">{t(`${LP}.contact.title`)} <span className="lp-contact__accent" style={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, color: '#A5765C' }}>{t(`${LP}.contact.titleAccent`)}</span></h2>
            <p className="lp-section-subtitle">{t(`${LP}.contact.subtitle`)}</p>
          </div>
          
          <motion.div 
            className="lp-contact__card"
            whileHover={hoverLift ? { y: -8 } : undefined}
            style={{ 
              background: 'white', 
              borderRadius: '40px', 
              padding: '60px 40px', 
              border: '1px solid rgba(165,118,92,0.15)',
              boxShadow: '0 40px 100px rgba(0,0,0,0.05)'
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '24px' }}>👋</div>
            <a className="lp-contact__email" href="mailto:contact@rintintin-booking.com" style={{ 
              fontFamily: 'Plus Jakarta Sans', 
              fontStyle: 'normal',
              fontSize: 'min(36px, 6vw)',
              color: '#A5765C',
              textDecoration: 'none',
              fontWeight: 700
            }}>
              contact@rintintin-booking.com
            </a>
            <p style={{ marginTop: '24px', color: '#64748b', fontSize: '16px', fontWeight: 500 }}>{t(`${LP}.contact.response`)}</p>
            <p style={{ marginTop: '16px', fontSize: '14px', color: '#64748b' }}>
              <Trans
                i18nKey={`${LP}.contact.individualRich`}
                ns="marketing"
                components={{
                  strong: <strong />,
                  link: (
                    <a
                      className="lp-contact__booking-link"
                      href="https://www.rintintin-booking.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#A5765C', fontWeight: 600, textDecoration: 'none' }}
                    />
                  ),
                }}
              />
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== FINAL SOCIAL PROOF ===== */}
      <section className="lp-proof" style={{ padding: '60px 24px 120px', background: '#fff', textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ maxWidth: '600px', margin: '0 auto' }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '24px' }}>
            {[1, 2, 3, 4, 5].map(i => (
              <Star key={i} fill="#f59e0b" color="#f59e0b" size={20} />
            ))}
          </div>
          <p style={{ 
            fontFamily: 'Plus Jakarta Sans', 
            fontSize: '18px', 
            fontWeight: 700, 
            color: '#1e293b', 
            marginBottom: '12px' 
          }}>
            {t(`${LP}.socialProof.quote`)}
          </p>
          <p style={{ 
            fontFamily: 'Inter', 
            fontSize: '15px', 
            color: '#64748b', 
            fontWeight: 500,
            marginBottom: '40px'
          }}>
            <Trans
              i18nKey={`${LP}.socialProof.adoptedRich`}
              ns="marketing"
              components={{
                highlight: <span style={{ color: '#A5765C', fontWeight: 800 }} />,
              }}
            />
          </p>
          
          <div style={{ 
            height: '1px', 
            width: '100px', 
            background: 'rgba(165,118,92,0.2)', 
            margin: '0 auto 40px' 
          }} />
          
          <div
            className="lp-proof__pills"
            style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '32px',
            opacity: 0.6,
            filter: 'grayscale(100%)'
          }}>
            <span style={{ fontWeight: 800, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b' }}>{t(`${LP}.socialProof.noCommitment`)}</span>
            <div style={{ width: '4px', height: '4px', background: '#cbd5e1', borderRadius: '50%' }} />
            <span style={{ fontWeight: 800, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b' }}>{t(`${LP}.socialProof.setup`)}</span>
            <div style={{ width: '4px', height: '4px', background: '#cbd5e1', borderRadius: '50%' }} />
            <span style={{ fontWeight: 800, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b' }}>{t(`${LP}.socialProof.support`)}</span>
          </div>
        </motion.div>
      </section>

      <div className="lp-social-proof-strip">
        <SocialProofStrip />
      </div>
      <p className="lp-proof__more-links" style={{ textAlign: 'center', margin: '24px 0 0', fontSize: 15 }}>
        <ProMarketingLink to="/faq">FAQ Rintintin Pro</ProMarketingLink>
        {' · '}
        <ProMarketingLink to="/avis-rintintin-pro">Avis & preuves</ProMarketingLink>
      </p>

      <BookingBridgeStrip />

      {/* ===== CINEMA MODE MODAL ===== */}
      <AnimatePresence>
        {cinemaMode && (
          <>
            <motion.div 
              className="cinema-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setCinemaMode(false)}
            />
            <motion.div 
              className="cinema-video-container"
              initial={{ opacity: 0, scale: 0.9, y: '-45%', x: '-50%' }}
              animate={{ opacity: 1, scale: 1, y: '-50%', x: '-50%' }}
              exit={{ opacity: 0, scale: 0.9, y: '-45%', x: '-50%' }}
            >
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/aDFaLWhfv7o?rel=0&modestbranding=1&autoplay=1"
                title={t(`${LP}.founder.videoTitleCinema`)}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </motion.div>
            <motion.button 
              className="cinema-exit-button"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              whileHover={hoverLift ? { scale: 1.05 } : undefined}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCinemaMode(false)}
              style={{
                position: 'fixed',
                top: '40px',
                right: '40px',
                zIndex: 100000,
                background: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '100px',
                fontFamily: 'Plus Jakarta Sans',
                fontWeight: 800,
                color: '#1e293b',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                width: 'auto',
                height: 'auto'
              }}
            >
              <X size={20} />
              {t('common.cinema.exit')}
            </motion.button>
          </>
        )}
      </AnimatePresence>

      <DemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  );
}
